import { generateAPI } from '@/shared/lib';
import { ContentsId } from '@/shared/model';
import { FetchState } from '@/types/fetchState';
import { ProjectKey } from '@/types/projectKey';
import { TabId } from '@/types/tabId';
import { defineStore } from 'pinia';

interface SysInfoState {
  colors: [key: { color: string, type: number}];
  contents: {
    id: ContentsId;
    isShow: boolean;
    tabName: string;
    // eslint-disable-next-line camelcase
    mesh_url?: string;
    meshMapId?: string;
    resultKey?: string;
    /** 選択できる観測所 */
    stations?: string[];
    subResult?: {
      enable: boolean;
      hasManual: boolean;
      manualName: string;
      manualUrl: string;
      resultKey: string;
      switchName: string;
      /** 操作方法を表示する観測所 */
      stations?: string[];
      defaultView?: boolean;
      hasReferenceLink?: boolean;
      ReferenceLinkName?: string;
      ReferenceLinkURL?: string;
    },
    /**
     * 実績値の画面表示設定
     * enable: 実績値のトグルの画面表示を有効にするかどうか（デフォルトはoff）
     * switchName: トグルの表示名
     * https://redmine.riskma.jp/attachments/download/622/%E5%AE%9F%E7%B8%BE%E9%9B%A8%E9%87%8F%E3%83%BB%E6%B5%81%E5%85%A5%E9%87%8F%E3%81%AE%E8%A1%A8%E7%A4%BA_250704.pdf
     */
    showObservation?: {
      enable: boolean;
      switchName: string;
    }
    formDownloadButton?: {
      enable: boolean,
      buttonName: string,
      // eslint-disable-next-line camelcase
      result_file: string,
      // eslint-disable-next-line camelcase
      target_stations: string[],
      // eslint-disable-next-line camelcase
      s3_path: string
    }
    /** ダムなし計算の表示 */
    isFloodControlEffect?: boolean;
    /** 手動計算の計算結果保存先 (例: results/${customResultKey}/ */
    customResultKey?: string;
  }[],
  projectKey: ProjectKey;

  // TODO: 型定義
  helpTeam: any;
  header: any;
  stationsMap: any;
}

export const useSysInfoStore = defineStore('sysInfo', {
  state: (): FetchState<SysInfoState> => ({
    data: null,
    loading: false,
    error: null,
  }),
  getters: {
    projectKey: (state) => state.data?.projectKey ?? null,
    contents: (state) => state.data?.contents ?? [],
    colors: (state) => state.data?.colors ?? null,
    helpTeam: (state) => state.data?.helpTeam ?? null,
    header: (state) => state.data?.header ?? null,
    stationsMap: (state) => state.data?.stationsMap ?? null,
    hasReferenceLink: (state) => {
      const result = state.data?.contents.find((content) => content.id === ContentsId.AutoCalculation);
      // hasReferenceLinkがfalseの場合はリンクを表示しない
      if (result?.subResult?.hasReferenceLink === false) {
        return false;
      }
      return true;
    },
    observationSwitchName: (state) => {
      const result = state.data?.contents.find((content) => content.id === ContentsId.AutoCalculation);
      return result?.showObservation?.switchName ?? '';
    },
    formDownloadButton: (state) => {
      const result = state.data?.contents.find((content) => content.id === ContentsId.AutoCalculation);
      return result?.formDownloadButton ?? null;
    },
    showEnsample: (state) => state.data?.contents.find((content) => content.id === ContentsId.EnsembleCalculation) ?? false,
    customResultKey: (state) => state.data?.contents.find((content) => content.id === ContentsId.CustomCalculation)?.customResultKey ?? '',
  },
  actions: {
    async fetch() {
      this.loading = true;
      try {
        this.data = await generateAPI().systemApi.sysInfoApiApiSysInfoGet();
      } catch (error) {
        this.error = error as Error;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    getSelectableStations(tabId: TabId) {
      const currentContent = this.contents.find((content) => content.id === tabId);
      return currentContent?.stations ?? [];
    },
    // 操作方法を表示するかどうか
    showSubResult(tabId: TabId, stationId: string) {
      const currentContent = this.contents.find((content) => content.id === tabId);
      const targetStations = currentContent?.subResult?.stations ?? [];
      if (targetStations.length > 0) {
        return currentContent?.subResult?.enable && targetStations.includes(stationId);
      }
      return currentContent?.subResult?.enable;
    },
    // 実績値の表示が有効かどうか
    isObservationEnabled(contentsId: TabId) {
      const result = this.data?.contents.find((content) => content.id === contentsId);
      return result?.showObservation?.enable ?? false;
    },
  },
});
